import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from './useLanguageSwitch';
import type { AppLanguage } from '@/shared/i18n/localeRoutes';
import germanFlag from '../../../assets/germany.png';
import usFlag from '../../../assets/united-states.png';

interface LanguageSelectorProps {
  className?: string;
}

interface LanguageOption {
  code: 'de' | 'en';
  flag: string;
  name: {
    de: string;
    en: string;
  };
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className = '' }) => {
  const { i18n } = useTranslation('common');
  const [hoveredLang, setHoveredLang] = useState<string | null>(null);
  const switchLanguage = useLanguageSwitch();

  // The mount effect that re-read localStorage is gone on purpose: i18n.ts
  // already applies the stored language at init, and on /de that effect would
  // have overwritten the URL's language with the stored one.
  const handleLanguageChange = (lang: AppLanguage) => {
    switchLanguage(lang);
  };

  const languages: LanguageOption[] = [
    { 
      code: 'de', 
      flag: germanFlag, 
      name: {
        de: 'Deutsch',
        en: 'German'
      }
    },
    { 
      code: 'en', 
      flag: usFlag, 
      name: {
        de: 'Englisch',
        en: 'English'
      }
    }
  ];

  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex items-center bg-muted rounded-lg p-1 space-x-1">
        {languages.map((lang) => {
          const isActive = i18n.language === lang.code;
          const isHovered = hoveredLang === lang.code;
          
          return (
            <div
              key={lang.code}
              className="relative group"
              onMouseEnter={() => setHoveredLang(lang.code)}
              onMouseLeave={() => setHoveredLang(null)}
            >
              <button
                onClick={() => handleLanguageChange(lang.code)}
                className={`
                  relative p-2 rounded-md transition-all duration-200 ease-out
                  transform hover:scale-110 active:scale-95
                  ${isActive 
                    ? 'bg-card shadow-md ring-1 ring-border'
                    : 'hover:bg-muted/60'
                  }
                  ${isHovered && !isActive ? 'shadow-sm' : ''}
                `}
                aria-label={`Switch to ${lang.name[i18n.language as 'de' | 'en'] || lang.name.en}`}
              >
                <img 
                  src={lang.flag} 
                  alt={lang.name[i18n.language as 'de' | 'en'] || lang.name.en}
                  className={`
                    w-5 h-5 rounded-sm transition-all duration-200
                    ${isActive 
                      ? 'opacity-100 brightness-110' 
                      : 'opacity-75 hover:opacity-100 grayscale-[0.2] hover:grayscale-0'
                    }
                  `}
                />
                
                {/* Active indicator */}
                {isActive && (
                  <div className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary rounded-full" />
                )}
              </button>
              
              {/* Tooltip */}
              <div className={`
                absolute top-full mt-2 left-1/2 transform -translate-x-1/2
                px-2 py-1 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap
                opacity-0 pointer-events-none transition-all duration-200 z-50
                ${isHovered ? 'opacity-100 translate-y-0' : 'translate-y-1'}
                before:content-[''] before:absolute before:bottom-full before:left-1/2 
                before:transform before:-translate-x-1/2 before:border-4 
                before:border-transparent before:border-b-gray-900
              `}>
                {lang.name[i18n.language as 'de' | 'en'] || lang.name.en}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LanguageSelector;
